import {Menu} from "primereact/menu";
import type {MenuItem} from "primereact/menuitem";
import {useRef} from "react";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";

import {useDocumentationDownload} from "../component/AvmDocumentationLink";
import {useRevaTranslation} from "../hooks/languageHooks";
import {useCheckPermissionAdmin, useCreatablePurposes} from "../hooks/permissionHooks";
import {selectLoggedIn} from "../redux/authSlice";
import {selectAppEnvironment} from "../redux/envSlice";
import {fetchAvmDocumentation, fetchAvmReleaseNotes, fetchRevaManual} from "../redux/pdfThunk";
import {REVA_MANUALS} from "../util/revaManuals";
import LanguageSwitcher from "./LanguageSwitcher";
import ThemeSwitcher from "./ThemeSwitcher";

interface FooterLinkProps {
    href: string;
    text: string;
    id: string;
}

function FooterLink({href, text, id}: FooterLinkProps) {
    return (
        <a
            className="no-underline mx-4 text-primary cursor-pointer"
            id={`footerLink${id}`}
            href={href}
            target="_blank"
            rel="noreferrer"
        >
            {text}
        </a>
    );
}

function AdminInfo() {
    const {t} = useTranslation();

    const environment = useSelector(selectAppEnvironment);
    const isAdmin = useCheckPermissionAdmin();

    if (!isAdmin) {
        return null;
    }

    let gitInfo = null;
    if (environment.hasGitInfo) {
        gitInfo = (
            <span className="footerGitInfo" title={environment.gitCommitMessage ?? undefined}>
                {t("footer.git", {
                    branch: environment.gitBranch,
                    commit: environment.gitCommitHashShort,
                    date: new Date(environment.gitCommitDate!),
                })}
            </span>
        );
    }

    let buildDateInfo = null;
    if (environment.buildDate) {
        buildDateInfo = (
            <span className="footerBuildInfo">{t("footer.build", {date: new Date(environment.buildDate)})}</span>
        );
    }

    let separator = null;
    if (environment.hasGitInfo && environment.buildDate) {
        separator = <span className="mx-1">&#x2022;</span>;
    }

    return (
        <span className="adminInfo text-muted">
            {gitInfo}
            {separator}
            {buildDateInfo}
        </span>
    );
}

/**
 * Menü „Dokumentation“ (#19302): REVA-Handbücher je Zweck, mit dem der Nutzer anlegen darf, dann AVM-Doku und
 * AVM-Releasenotes (#19432). Alles Downloads über den REVA-Proxy mit den Credentials des eingeloggten Nutzers — auf
 * der Login-Seite bleibt das Menü weg.
 * @returns Knopf mit Popup-Menü oder null
 */
function DocumentationMenu() {
    const {t, language} = useRevaTranslation();
    const loggedIn = useSelector(selectLoggedIn);
    const purposes = useCreatablePurposes();
    const menuRef = useRef<Menu | null>(null);
    const {download, downloading, toast} = useDocumentationDownload();

    if (!loggedIn) {
        return null;
    }

    const manualItems: MenuItem[] = purposes.flatMap((purpose) => {
        const manual = REVA_MANUALS[purpose];
        if (!manual) return [];
        return [
            {
                label: t(manual.labelKey),
                command: () => void download(() => fetchRevaManual(purpose, language), manual.fallbackFilename),
            },
        ];
    });
    const model: MenuItem[] = [
        ...manualItems,
        {
            label: t("value.info.docu"),
            command: () => void download(fetchAvmDocumentation, "VALUE_AVM_dokumentation.pdf"),
        },
        {
            label: t("value.info.relasenotes"),
            command: () => void download(fetchAvmReleaseNotes, "VALUE_AVM_releasenotes.pdf"),
        },
    ];

    return (
        <>
            {/* Breite nach Inhalt statt der festen 12.5rem von PrimeReact: „Anwenderhandbuch Bewertung“ bleibt einzeilig. */}
            <Menu
                id="footerDocumentationMenu"
                model={model}
                popup
                ref={menuRef}
                style={{width: "auto", minWidth: "12.5rem", whiteSpace: "nowrap"}}
            />
            {/* [font:inherit]: Preflight ist aus, sonst bekommt der Button die kleinere Browser-Formularschrift statt der des umgebenden Textes. */}
            <button
                type="button"
                id="footerLinkDocumentation"
                className="[font:inherit] p-0 bg-transparent border-none no-underline mx-4 text-primary cursor-pointer"
                disabled={downloading}
                onClick={(e) => menuRef.current?.toggle(e)}
                aria-controls="footerDocumentationMenu"
                aria-haspopup
            >
                {t("footer.documentation")}
            </button>
            {toast}
        </>
    );
}

function Footer() {
    const {t} = useTranslation();

    return (
        <footer className="AppFooter bg-surface-50 dark:bg-surface-950 grow-0">
            <div className="grid grid-cols-12 gap-4 mt-0 mx-1">
                <div className="reva-footer-admin col-span-12 xl:col-span-4 text-xs">
                    <div className="p-4">
                        <AdminInfo />
                    </div>
                </div>
                <div className="reva-footer-links col-span-12 md:col-span-8 xl:col-span-4 text-center inner">
                    <div className="p-4">
                        <FooterLink
                            text={t("footer.it_status")}
                            id="footerLinkItStatus"
                            href="https://status.value-marktdaten.de"
                        />
                        <FooterLink
                            text={t("footer.imprint")}
                            id="footerLinkImpressum"
                            href="https://www.value-marktdaten.de/impressum"
                        />
                        <FooterLink
                            text={t("footer.privacy")}
                            id="footerLinkDatenschutz"
                            href="https://www.value-marktdaten.de/datenschutz"
                        />
                        <FooterLink
                            text={t("footer.contact")}
                            id="footerLinkKontakt"
                            href="https://www.value-marktdaten.de/kontakt"
                        />
                        <DocumentationMenu />
                    </div>
                </div>
                <div className="reva-footer-switchers col-span-12 md:col-span-2 xl:col-span-4 text-right">
                    <div className="p-2">
                        <LanguageSwitcher />
                        <ThemeSwitcher />
                    </div>
                </div>
            </div>
        </footer>
    );
}

export default Footer;
